// Shared copy + URL for a rejected Google / email-link sign-in.
// Both methods call authorizeVerifiedIdentity; the login page must keep this
// message visible (query string + sessionStorage), not a one-shot flash.

export type LoginRejectReason = "not_authorized" | "inactive";

export function isLoginRejectReason(value: string | null | undefined): value is LoginRejectReason {
  return value === "not_authorized" || value === "inactive";
}

/**
 * Former staff test logins. Reject even if a Users row still exists (Airtable
 * leftover or an old roster seed). Official SoftR emails stay on the allowlist.
 */
export const REVOKED_LOGIN_EMAILS = [
  "aculich@gmail.com",
  "aaron@asemicarche.com",
] as const;

const REVOKED_LOGIN_EMAIL_SET = new Set<string>(REVOKED_LOGIN_EMAILS);

export function isRevokedLoginEmail(email: string): boolean {
  return REVOKED_LOGIN_EMAIL_SET.has(email.trim().toLowerCase());
}

export function loginRejectMessage(reason: LoginRejectReason): string {
  if (reason === "inactive") {
    return "This account exists but is marked inactive. Contact the RRID team if you believe this is a mistake.";
  }
  return "This email is not on the portal allowlist. Use the same address you already use on SoftR, or ask Angel (rrid-help@berkeley.edu) to add you.";
}

/** Browser path the API redirects to after Google (or the email-link page links back). */
export function unauthorizedLoginPath(reason: LoginRejectReason, email?: string): string {
  const params = new URLSearchParams({ reason });
  const trimmed = email?.trim().toLowerCase();
  if (trimmed) params.set("email", trimmed);
  return `/login?${params.toString()}`;
}

export function unauthorizedLoginRedirect(
  webOrigin: string,
  reason: LoginRejectReason,
  email?: string,
): string {
  return `${webOrigin.replace(/\/$/, "")}${unauthorizedLoginPath(reason, email)}`;
}
